import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, Pencil, Trash2, BarChart2, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import type { VanityLink } from "../../api/_lib/types";

const BASE_URL = window.location.origin;

interface Props {
  links: VanityLink[];
  loading: boolean;
  onEdit: (link: VanityLink) => void;
  onDelete: (slug: string) => void;
  onStats: (link: VanityLink) => void;
  onCreate: () => void;
}

export function LinkTable({ links, loading, onEdit, onDelete, onStats, onCreate }: Props) {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (slug: string) => {
    await navigator.clipboard.writeText(`${BASE_URL}/${slug}`);
    setCopied(slug);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-14 rounded-lg bg-muted/50 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{links.length} link{links.length !== 1 ? "s" : ""}</p>
        <Button size="sm" onClick={onCreate} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          New link
        </Button>
      </div>

      {links.length === 0 && (
        <div className="rounded-xl border border-dashed border-border py-16 text-center">
          <p className="text-muted-foreground text-sm">No links yet.</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={onCreate}>
            Create your first link
          </Button>
        </div>
      )}

      {links.length > 0 && (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Slug</th>
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs hidden md:table-cell">Destination</th>
                <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs">Clicks</th>
                <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs hidden sm:table-cell">Created</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {links.map((link, i) => (
                <tr
                  key={link.slug}
                  className={`group border-b border-border last:border-0 transition-colors hover:bg-muted/20 ${i % 2 === 0 ? "" : ""}`}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{link.slug}</span>
                    {(link.utmSource || link.utmMedium) && (
                      <span className="ml-2 text-[10px] text-muted-foreground hidden lg:inline">
                        utm: {[link.utmSource, link.utmMedium].filter(Boolean).join("/")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-muted-foreground truncate block max-w-[240px]" title={link.url}>
                      {link.url}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Badge variant="secondary" className="tabular-nums font-mono text-xs">
                      {link.totalClicks.toLocaleString()}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-muted-foreground hidden sm:table-cell">
                    {new Date(link.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <IconButton title="Copy link" onClick={() => copy(link.slug)}>
                        {copied === link.slug ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                      </IconButton>
                      <IconButton title="Stats" onClick={() => onStats(link)}>
                        <BarChart2 className="h-3.5 w-3.5" />
                      </IconButton>
                      <IconButton title="Edit" onClick={() => onEdit(link)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </IconButton>
                      <IconButton title="Delete" onClick={() => onDelete(link.slug)} className="hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function IconButton({ children, onClick, title, className = "" }: { children: React.ReactNode; onClick: () => void; title: string; className?: string }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors ${className}`}
    >
      {children}
    </button>
  );
}
